import React, { useState, useEffect } from 'react';
import Sidebar from '@/components/Sidebar';
import MobileNav from '@/components/MobileNav';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Shield, Users, Crown } from 'lucide-react';
import { getUsers, updateUserRole } from '@/lib/api';
import { toast } from 'sonner';

const AdminPanel = ({ user }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await getUsers();
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, currentRole) => {
    const newRole = currentRole === 'admin' ? 'member' : 'admin';
    const action = newRole === 'admin' ? 'promote to admin' : 'demote to member';
    
    if (!confirm(`Are you sure you want to ${action} this user?`)) {
      return;
    }

    try {
      await updateUserRole(userId, newRole);
      toast.success(`User ${newRole === 'admin' ? 'promoted' : 'demoted'} successfully!`);
      loadUsers();
    } catch (error) {
      toast.error('Failed to update user role');
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <Shield className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-foreground mb-2">Access Denied</h2>
          <p className="text-muted-foreground">Only administrators can access this page</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <Sidebar user={user} />
      
      <div className="flex-1 flex flex-col overflow-hidden md:ml-64">
        <header className="bg-white border-b border-border px-6 py-4">
          <div>
            <h2 className="text-2xl font-bold text-foreground flex items-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Shield className="h-6 w-6 mr-2 text-primary" />
              Admin Panel
            </h2>
            <p className="text-sm text-muted-foreground">Manage users and permissions</p>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6 pb-24 md:pb-6">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="max-w-6xl mx-auto space-y-6">
              {/* Stats */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Total Users</CardTitle>
                    <Users className="h-4 w-4 text-primary" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-foreground">{users.length}</div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Admins</CardTitle>
                    <Crown className="h-4 w-4 text-amber-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-foreground">
                      {users.filter(u => u.role === 'admin').length}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* User List */}
              <Card>
                <CardHeader>
                  <CardTitle>All Users</CardTitle>
                  <CardDescription>Manage user roles and permissions</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {users.map((usr) => (
                      <div 
                        key={usr.user_id} 
                        className="flex items-center justify-between p-4 bg-accent/20 rounded-lg hover:bg-accent/30 transition-colors"
                        data-testid={`user-${usr.user_id}`}
                      >
                        <div className="flex items-center space-x-4 flex-1">
                          <img
                            src={usr.picture || 'https://via.placeholder.com/48'}
                            alt={usr.name}
                            className="h-12 w-12 rounded-full"
                          />
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <p className="font-semibold text-foreground">{usr.name}</p>
                              {usr.role === 'admin' && (
                                <Crown className="h-4 w-4 text-amber-600" />
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">{usr.email}</p>
                          </div>
                        </div>

                        <div className="flex items-center space-x-3">
                          <Badge 
                            variant={usr.role === 'admin' ? 'default' : 'secondary'}
                            className={usr.role === 'admin' ? 'bg-amber-100 text-amber-800 border-amber-200' : ''}
                          >
                            {usr.role === 'admin' ? 'Admin' : 'Member'}
                          </Badge>

                          {usr.user_id !== user.user_id && (
                            <Button
                              onClick={() => handleRoleChange(usr.user_id, usr.role)}
                              variant={usr.role === 'admin' ? 'outline' : 'default'}
                              size="sm"
                              data-testid={`change-role-${usr.user_id}`}
                            >
                              {usr.role === 'admin' ? 'Demote' : 'Make Admin'}
                            </Button>
                          )}

                          {usr.user_id === user.user_id && (
                            <Badge variant="outline" className="bg-primary/10 text-primary">
                              You
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Security Info */}
              <Card className="bg-amber-50 border-amber-200">
                <CardHeader>
                  <CardTitle className="flex items-center text-amber-900">
                    <Shield className="h-5 w-5 mr-2" />
                    Security Note
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm text-amber-900">
                    <p>✅ <strong>Only admins</strong> can access this page and change roles</p>
                    <p>✅ <strong>You cannot demote yourself</strong> - prevents accidental lockout</p>
                    <p>✅ <strong>All changes are logged</strong> for security audit</p>
                    <p>⚠️ <strong>Make sure you trust</strong> anyone you promote to admin</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </main>

        <MobileNav user={user} />
      </div>
    </div>
  );
};

export default AdminPanel;
